import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Tags, Plus, Pencil, Trash2, Check, X, Loader2 } from "lucide-react";
import { FALLBACK_CATEGORY, categoryHex, setActiveCategories } from "../lib/categories";
import { useCategories } from "../lib/useCategories";

// Per-user category management (add / rename / delete) — fits any project.
export const CategoryManager = () => {
  const { categories, refresh } = useCategories();
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // category being renamed
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const r = await api.post("/categories", { name: newName.trim() });
      setActiveCategories(r.data?.categories);
      toast.success("تم إضافة الصنف");
      setNewName("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر إضافة الصنف");
    } finally {
      setAdding(false);
    }
  };

  const rename = async (oldName) => {
    if (!editValue.trim() || editValue.trim() === oldName) {
      setEditing(null);
      return;
    }
    setBusy(oldName);
    try {
      const r = await api.put("/categories/rename", { old_name: oldName, new_name: editValue.trim() });
      setActiveCategories(r.data?.categories);
      const n = r.data?.invoices_updated || 0;
      toast.success(n > 0 ? `تم تعديل الصنف وتحديث ${n} فاتورة` : "تم تعديل الصنف");
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التعديل");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (name) => {
    setBusy(name);
    try {
      const r = await api.delete(`/categories/${encodeURIComponent(name)}`);
      setActiveCategories(r.data?.categories);
      toast.success("تم حذف الصنف — فواتيره القديمة ستظهر تحت \"أخرى (تحديد)\"");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6" data-testid="category-manager">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-md bg-[#F5F4EF] flex items-center justify-center">
          <Tags className="w-5 h-5 text-[#1F3A33]" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-readex font-medium text-[#1A1C19]">أصناف الفواتير</div>
          <div className="text-xs text-[#8A8D86]">أضف أو عدّل أو احذف الأصناف لتناسب مشروعك — تُستخدم في التصنيف التلقائي والفلترة والتقارير</div>
        </div>
      </div>

      <ul className="divide-y divide-[#1A1C19]/5 mb-4" data-testid="categories-list">
        {categories.map((cat) => {
          const isFallback = cat === FALLBACK_CATEGORY;
          return (
            <li key={cat} className="flex items-center gap-2 py-2.5" data-testid={`category-item-${cat}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: categoryHex(cat) }} />
              {editing === cat ? (
                <>
                  <input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 border border-[#1F3A33]/40 rounded-md px-3 py-1.5 text-sm focus:outline-none"
                    autoFocus
                    data-testid="category-rename-input"
                    onKeyDown={(e) => e.key === "Enter" && rename(cat)}
                  />
                  <button onClick={() => rename(cat)} disabled={busy === cat}
                    className="p-1.5 text-[#1F3A33] hover:bg-[#EAF2ED] rounded-md" title="حفظ" data-testid="category-rename-save">
                    {busy === cat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </button>
                  <button onClick={() => setEditing(null)} className="p-1.5 text-[#5C605A] hover:bg-[#F0EFEB] rounded-md" title="إلغاء">
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm font-readex text-[#1A1C19]">
                    {cat}
                    {isFallback && <span className="text-[10px] text-[#8A8D86] me-2 ms-2">(افتراضي — لا يُحذف)</span>}
                  </span>
                  {!isFallback && (
                    <>
                      <button onClick={() => { setEditing(cat); setEditValue(cat); }} disabled={!!busy}
                        className="p-1.5 text-[#5C605A] hover:bg-[#F0EFEB] rounded-md" title="إعادة تسمية" data-testid={`category-edit-${cat}`}>
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button onClick={() => remove(cat)} disabled={!!busy}
                        className="p-1.5 text-[#C84C31] hover:bg-[#C84C31]/10 rounded-md" title="حذف" data-testid={`category-delete-${cat}`}>
                        {busy === cat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
                      </button>
                    </>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      <form onSubmit={add} className="flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="اسم صنف جديد…"
          className="flex-1 border border-[#1A1C19]/15 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#1F3A33]"
          data-testid="category-add-input"
        />
        <button type="submit" disabled={adding || !newName.trim()}
          className="bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 rounded-md px-5 py-2.5 font-readex font-medium inline-flex items-center gap-2 text-sm"
          data-testid="category-add-btn">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          إضافة
        </button>
      </form>
    </div>
  );
};
